import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { MessageSquare, Mail, Phone, X, RefreshCw } from 'lucide-react';
import ServiceSelector from './ServiceSelector';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE_URL = `${BACKEND_URL}/api`;

const EditAppointmentModal = ({ calendarId, calendar, appointment, services, onClose, getAuthHeaders, isCustomer, onCancelClick }) => {
  const { runningUser } = useAuth();
  const [customers, setCustomers] = useState([]);
  const [servers, setServers] = useState([]);
  
  // Extract all customer IDs from related appointments (for multi-customer bookings)
  const getInitialCustomerIds = () => {
    if (appointment?.relatedAppointments && appointment.relatedAppointments.length > 1) {
      // Multi-customer booking - extract all customer IDs
      return appointment.relatedAppointments.map(apt => apt.customer_id);
    }
    // Single customer booking
    return appointment?.customer_id ? [appointment.customer_id] : [];
  };
  
  const [formData, setFormData] = useState({
    customer_ids: getInitialCustomerIds(),
    service_id: appointment?.service_id || '',
    server_id: appointment?.server_id || '',
    date: appointment?.start_time?.split('T')[0] || '',
    time: appointment?.start_time?.split('T')[1]?.substring(0, 5) || '',
    notes: appointment?.notes || ''
  });
  const [selectedService, setSelectedService] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Store the original related appointments for update/delete operations
  const [relatedAppointments] = useState(appointment?.relatedAppointments || [appointment]);
  
  // Check if selected service allows multiple customers
  const allowsMultipleCustomers = selectedService && selectedService.customers_allowed > 1;
  const maxCustomers = selectedService?.customers_allowed || 1;
  
  // Send Message state
  const [showMessageForm, setShowMessageForm] = useState(false);
  const [messageRecipient, setMessageRecipient] = useState(null);
  const [messageForm, setMessageForm] = useState({
    channel: 'email',
    subject: '',
    message: ''
  });
  const [sendingMessage, setSendingMessage] = useState(false);
  const [messageSuccess, setMessageSuccess] = useState('');

  useEffect(() => {
    // Only fetch customers for admins, not for customer users
    if (!isCustomer) {
      fetchCustomers();
    }
    
    // Fetch servers if calendar supports multiple servers
    if (calendar?.multiple_servers) {
      fetchServers();
    }
  }, [isCustomer, calendar]);

  useEffect(() => {
    if (services.length > 0 && formData.service_id) {
      const service = services.find(s => s.id === formData.service_id);
      setSelectedService(service);
    }
  }, [services, formData.service_id]);

  const fetchCustomers = async () => {
    try {
      // Fetch customers registered to THIS calendar only (same as Create Appointment)
      const customersUrl = calendarId 
        ? `${API_BASE_URL}/calendars/${calendarId}/customers`
        : `${API_BASE_URL}/admin/users`;
      
      const customersRes = await axios.get(customersUrl, {
        headers: getAuthHeaders()
      });
      
      // Handle both response formats and filter to customers only
      const customersList = (customersRes.data.data || [])
        .filter(u => u.role === 'customer' || u.role === 'non_registered_customer')
        .sort((a, b) => (a.full_name || '').localeCompare(b.full_name || ''));
      setCustomers(customersList);
    } catch (err) {
      console.error('Error fetching customers:', err);
      // Only set error for admins
      if (!isCustomer) {
        setError('Failed to load customers');
      }
    }
  };

  const fetchServers = async () => {
    try {
      const serversRes = await axios.get(`${API_BASE_URL}/calendar-servers/${calendarId}/servers`, {
        headers: getAuthHeaders()
      });
      
      // Get assigned servers only
      const assignedServers = serversRes.data.data.assigned || [];
      setServers(assignedServers);
    } catch (err) {
      console.error('Error fetching servers:', err);
      // Don't show error to user, just don't show server selection
    }
  };

  // Open send message form
  const openSendMessage = async () => {
    try {
      // Determine recipient based on user role
      let recipient;
      if (isCustomer) {
        // Customer sending to Admin - fetch calendar owner info
        const calendarRes = await axios.get(`${API_BASE_URL}/calendars/${calendarId}`, {
          headers: getAuthHeaders()
        });
        const ownerRes = await axios.get(`${API_BASE_URL}/admin/users/${calendarRes.data.owner_id}`, {
          headers: getAuthHeaders()
        }).catch(() => null);
        
        recipient = ownerRes?.data?.data || {
          full_name: 'Calendar Admin',
          email: calendarRes.data.owner_email || '',
          phone: null
        };
      } else {
        // Admin sending to Customer - find customer from appointment
        const customer = customers.find(c => c.id === appointment?.customer_id);
        if (!customer) {
          // Fetch customer info
          const customerRes = await axios.get(`${API_BASE_URL}/admin/users/${appointment?.customer_id}`, {
            headers: getAuthHeaders()
          }).catch(() => null);
          recipient = customerRes?.data?.data || { full_name: 'Customer', email: '' };
        } else {
          recipient = customer;
        }
      }
      
      setMessageRecipient(recipient);
      setMessageForm({
        channel: 'email',
        subject: `Regarding your appointment on ${formData.date}`,
        message: ''
      });
      setShowMessageForm(true);
      setMessageSuccess('');
    } catch (err) {
      console.error('Error preparing message form:', err);
      setError('Failed to load recipient information');
    }
  };

  // Send message
  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!appointment?.id) return;
    
    setSendingMessage(true);
    setError('');
    setMessageSuccess('');
    
    try {
      const response = await axios.post(
        `${API_BASE_URL}/appointments/${appointment.id}/send-message`,
        {
          subject: messageForm.channel === 'email' ? messageForm.subject : null,
          message: messageForm.message,
          channel: messageForm.channel
        },
        { headers: getAuthHeaders() }
      );
      
      if (response.data.success) {
        setMessageSuccess(`Message sent successfully to ${messageRecipient?.full_name || 'recipient'}`);
        setShowMessageForm(false);
        setMessageForm({ channel: 'email', subject: '', message: '' });
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to send message');
    } finally {
      setSendingMessage(false);
    }
  };

  const handleServiceChange = (serviceId) => {
    const service = services.find(s => s.id === serviceId);
    
    // When service changes, reset customer selection if switching between single/multi
    const newAllowsMultiple = service && service.customers_allowed > 1;
    const currentAllowsMultiple = selectedService && selectedService.customers_allowed > 1;
    
    if (newAllowsMultiple !== currentAllowsMultiple) {
      // Reset customer selection when switching modes
      setFormData(prev => ({ 
        ...prev, 
        service_id: serviceId,
        customer_ids: [] 
      }));
    } else {
      setFormData(prev => ({ ...prev, service_id: serviceId }));
    }
    
    setSelectedService(service);
  };

  // Handle customer toggle for multi-select mode
  const handleCustomerToggle = (customerId) => {
    setFormData(prev => {
      const isSelected = prev.customer_ids.includes(customerId);
      let newCustomerIds;
      
      if (isSelected) {
        // Remove customer
        newCustomerIds = prev.customer_ids.filter(id => id !== customerId);
      } else {
        // Add customer (respecting max limit)
        if (prev.customer_ids.length < maxCustomers) {
          newCustomerIds = [...prev.customer_ids, customerId];
        } else {
          // At max capacity - don't add
          return prev;
        }
      }
      
      return { ...prev, customer_ids: newCustomerIds };
    });
  };

  // Handle single customer selection (radio button mode)
  const handleSingleCustomerSelect = (customerId) => {
    setFormData(prev => ({ ...prev, customer_ids: [customerId] }));
  };

  const calculateEndTime = () => {
    if (!selectedService || !formData.time) return '';
    
    const [hours, minutes] = formData.time.split(':').map(Number);
    const durationMinutes = selectedService.service_duration || 60;
    const totalMinutes = hours * 60 + minutes + durationMinutes;
    const endHours = Math.floor(totalMinutes / 60);
    const endMinutes = totalMinutes % 60;
    
    return `${endHours.toString().padStart(2, '0')}:${endMinutes.toString().padStart(2, '0')}`;
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // Validate customer selection
      if (formData.customer_ids.length === 0) {
        setError('Please select at least one customer');
        setLoading(false);
        return;
      }

      const startTime = `${formData.date}T${formData.time}:00`;
      const endTime = `${formData.date}T${calculateEndTime()}:00`;

      const baseUpdateData = {
        service_id: formData.service_id,
        start_time: startTime,
        end_time: endTime,
        notes: formData.notes
      };

      // Add server_id if calendar supports multiple servers and a server is selected
      if (calendar?.multiple_servers && formData.server_id) {
        baseUpdateData.server_id = formData.server_id;
      }

      // For multi-customer bookings, we need to:
      // 1. Update existing appointments that are still selected
      // 2. Create new appointments for newly added customers
      // 3. Delete appointments for removed customers
      
      const originalCustomerIds = relatedAppointments.map(apt => apt.customer_id);
      const newCustomerIds = formData.customer_ids;
      
      // Customers to keep (update their appointments)
      const customersToUpdate = newCustomerIds.filter(id => originalCustomerIds.includes(id));
      // Customers to add (create new appointments)
      const customersToAdd = newCustomerIds.filter(id => !originalCustomerIds.includes(id));
      // Customers to remove (delete their appointments)
      const customersToRemove = originalCustomerIds.filter(id => !newCustomerIds.includes(id));

      // Update existing appointments (skip notification - they already received confirmation)
      for (const customerId of customersToUpdate) {
        const existingApt = relatedAppointments.find(apt => apt.customer_id === customerId);
        if (existingApt) {
          await axios.put(
            `${API_BASE_URL}/appointments/${existingApt.id}`,
            { ...baseUpdateData, customer_id: customerId, skip_notification: true },
            { headers: getAuthHeaders() }
          );
        }
      }

      // Create new appointments for added customers (they WILL receive confirmation)
      for (const customerId of customersToAdd) {
        await axios.post(
          `${API_BASE_URL}/appointments`,
          {
            calendar_id: calendarId,
            customer_id: customerId,
            ...baseUpdateData,
            status: 'scheduled'
          },
          { headers: getAuthHeaders() }
        );
      }

      // Cancel/delete appointments for removed customers
      for (const customerId of customersToRemove) {
        const aptToRemove = relatedAppointments.find(apt => apt.customer_id === customerId);
        if (aptToRemove) {
          await axios.patch(
            `${API_BASE_URL}/appointments/${aptToRemove.id}/cancel`,
            {},
            { headers: getAuthHeaders() }
          );
        }
      }

      onClose();
    } catch (err) {
      console.error('Error updating appointment:', err);
      setError(err.response?.data?.detail || 'Failed to update appointment');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    // Show cancel confirmation modal instead of window.confirm
    // The actual deletion will be handled by the confirmation modal
    setError('');
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b flex justify-between items-start">
          <div>
            <h2 className="text-2xl font-bold text-gray-800">Edit Appointment</h2>
            <p className="text-gray-600 mt-1">
              ID: {appointment?.id?.substring(0, 8)}...
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleUpdate} className="p-6 space-y-4">
          {error && (
            <div className="p-3 bg-red-100 border border-red-400 text-red-700 rounded">
              {error}
            </div>
          )}

          {/* SERVICE SELECTION - Moved above Customer */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Service *
            </label>
            <ServiceSelector
              services={services}
              selectedServiceId={formData.service_id}
              onServiceChange={handleServiceChange}
              required
            />
          </div>

          {selectedService && (
            <div className="bg-gray-50 p-3 rounded-md">
              <p className="text-sm text-gray-600">
                <strong>Duration:</strong> {selectedService.service_duration} minutes
              </p>
              <p className="text-sm text-gray-600">
                <strong>End Time:</strong> {calculateEndTime()}
              </p>
              {allowsMultipleCustomers && (
                <p className="text-sm text-blue-600 mt-1">
                  <strong>Note:</strong> This service allows up to {maxCustomers} participants
                </p>
              )}
            </div>
          )}

          {/* CUSTOMER SELECTION - Now below Service with dynamic single/multi mode */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {allowsMultipleCustomers ? (
                <>Customer(s) * <span className="text-gray-400 font-normal">({formData.customer_ids.length}/{maxCustomers} selected)</span></>
              ) : (
                'Customer *'
              )}
            </label>
            {isCustomer ? (
              // Display-only field for customers
              <div className="w-full px-4 py-2 border border-gray-300 rounded-md bg-gray-100 text-gray-700 font-mono text-sm">
                👤 {runningUser?.full_name} | {runningUser?.email}
              </div>
            ) : !formData.service_id ? (
              // Prompt to select service first
              <div className="p-4 text-center text-gray-500 border border-gray-200 rounded-md bg-gray-50">
                Please select a service first
              </div>
            ) : (
              // Dynamic selection based on service's customers_allowed
              <div className="border border-gray-200 rounded-md max-h-48 overflow-y-auto bg-white">
                {customers.length === 0 ? (
                  <div className="p-4 text-center text-gray-500">No customers available</div>
                ) : (
                  <div className="divide-y">
                    {customers.map(customer => {
                      const isSelected = formData.customer_ids.includes(customer.id);
                      const isDisabled = !isSelected && formData.customer_ids.length >= maxCustomers;
                      
                      return (
                        <label
                          key={customer.id}
                          className={`flex items-center p-3 cursor-pointer transition-colors ${
                            isSelected 
                              ? 'bg-yellow-50 hover:bg-yellow-100' 
                              : isDisabled
                                ? 'bg-gray-100 cursor-not-allowed opacity-50'
                                : 'hover:bg-gray-50'
                          }`}
                        >
                          {allowsMultipleCustomers ? (
                            // Checkbox for multi-select
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => handleCustomerToggle(customer.id)}
                              disabled={isDisabled}
                              className="w-4 h-4 text-yellow-500 border-gray-300 rounded focus:ring-yellow-500"
                            />
                          ) : (
                            // Radio button for single-select
                            <input
                              type="radio"
                              name="customer_selection"
                              checked={isSelected}
                              onChange={() => handleSingleCustomerSelect(customer.id)}
                              className="w-4 h-4 text-yellow-500 border-gray-300 focus:ring-yellow-500"
                            />
                          )}
                          <div className="ml-3 flex-1">
                            <div className="flex items-center justify-between">
                              <div className="text-sm font-medium text-gray-900">{customer.full_name}</div>
                              {/* Show confirmation status for existing customers */}
                              {(() => {
                                const existingApt = relatedAppointments.find(apt => apt.customer_id === customer.id);
                                if (existingApt) {
                                  return existingApt.confirmation_sent ? (
                                    <span className="text-xs text-green-600 flex items-center">
                                      <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                      </svg>
                                      Notified
                                    </span>
                                  ) : (
                                    <span className="text-xs text-orange-500 flex items-center">
                                      <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                                      </svg>
                                      Pending
                                    </span>
                                  );
                                }
                                return null;
                              })()}
                            </div>
                            <div className="text-xs text-gray-500">{customer.email}</div>
                          </div>
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Date *
              </label>
              <input
                type="date"
                value={formData.date}
                onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Start Time *
              </label>
              <input
                type="time"
                value={formData.time}
                onChange={(e) => setFormData({ ...formData, time: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                required
              />
            </div>
          </div>

          {calendar?.multiple_servers && servers.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Server (Optional)
              </label>
              <select
                value={formData.server_id}
                onChange={(e) => setFormData({ ...formData, server_id: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
              >
                <option value="">Auto-assign server</option>
                {servers.map(server => (
                  <option key={server.id} value={server.id}>
                    {server.server_name} {server.email ? `(${server.email})` : ''}
                  </option>
                ))}
              </select>
              <p className="text-xs text-gray-500 mt-1">
                Leave as &quot;Auto-assign&quot; to let the system choose an available server
              </p>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Notes
            </label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
              rows="3"
              placeholder="Any additional notes..."
            />
          </div>

          {/* Send Message Section */}
          {!showMessageForm ? (
            <div className="pt-4 border-t">
              <button
                type="button"
                onClick={openSendMessage}
                className="flex items-center text-green-600 hover:text-green-700 transition-colors"
                data-testid="send-message-from-appointment"
              >
                <MessageSquare className="w-5 h-5 mr-2" />
                Send Message to {isCustomer ? 'Admin' : 'Customer'}
              </button>
              {messageSuccess && (
                <p className="mt-2 text-sm text-green-600">{messageSuccess}</p>
              )}
            </div>
          ) : (
            <div className="pt-4 border-t space-y-4">
              <div className="flex justify-between items-center">
                <h3 className="font-semibold text-gray-800">
                  Send Message to {messageRecipient?.full_name || (isCustomer ? 'Admin' : 'Customer')}
                </h3>
                <button
                  type="button"
                  onClick={() => setShowMessageForm(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              {/* Recipient Info */}
              <div className="text-sm text-gray-600 bg-gray-50 p-2 rounded">
                <div className="flex items-center space-x-3">
                  {messageRecipient?.email && (
                    <span className="flex items-center">
                      <Mail className="w-3 h-3 mr-1" />
                      {messageRecipient.email}
                    </span>
                  )}
                  {messageRecipient?.phone && (
                    <span className="flex items-center">
                      <Phone className="w-3 h-3 mr-1" />
                      {messageRecipient.phone}
                    </span>
                  )}
                </div>
              </div>
              
              {/* Channel Selection */}
              <div className="flex space-x-3">
                <label className={`flex items-center p-2 border rounded cursor-pointer transition-colors ${
                  messageForm.channel === 'email' 
                    ? 'border-yellow-500 bg-yellow-50' 
                    : 'border-gray-300 hover:border-gray-400'
                } ${!messageRecipient?.email ? 'opacity-50 cursor-not-allowed' : ''}`}>
                  <input
                    type="radio"
                    name="msg_channel"
                    value="email"
                    checked={messageForm.channel === 'email'}
                    onChange={(e) => setMessageForm({...messageForm, channel: e.target.value})}
                    disabled={!messageRecipient?.email}
                    className="sr-only"
                  />
                  <Mail className="w-4 h-4 mr-1 text-blue-600" />
                  <span className="text-sm">Email</span>
                </label>
                <label className={`flex items-center p-2 border rounded cursor-pointer transition-colors ${
                  messageForm.channel === 'sms' 
                    ? 'border-yellow-500 bg-yellow-50' 
                    : 'border-gray-300 hover:border-gray-400'
                } ${(!messageRecipient?.phone || !messageRecipient?.accepts_sms) ? 'opacity-50 cursor-not-allowed' : ''}`}>
                  <input
                    type="radio"
                    name="msg_channel"
                    value="sms"
                    checked={messageForm.channel === 'sms'}
                    onChange={(e) => setMessageForm({...messageForm, channel: e.target.value})}
                    disabled={!messageRecipient?.phone || !messageRecipient?.accepts_sms}
                    className="sr-only"
                  />
                  <Phone className="w-4 h-4 mr-1 text-green-600" />
                  <span className="text-sm">SMS</span>
                </label>
              </div>
              
              {messageRecipient?.phone && !messageRecipient?.accepts_sms && (
                <p className="text-xs text-orange-500">Recipient has not consented to SMS</p>
              )}
              
              {/* Subject (email only) */}
              {messageForm.channel === 'email' && (
                <input
                  type="text"
                  value={messageForm.subject}
                  onChange={(e) => setMessageForm({...messageForm, subject: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-yellow-500 focus:border-transparent text-sm"
                  placeholder="Subject..."
                  required
                />
              )}
              
              {/* Message */}
              <textarea
                value={messageForm.message}
                onChange={(e) => setMessageForm({...messageForm, message: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-yellow-500 focus:border-transparent text-sm"
                rows="3"
                placeholder="Your message..."
                maxLength={messageForm.channel === 'sms' ? 160 : undefined}
                required
              />
              {messageForm.channel === 'sms' && (
                <p className="text-xs text-gray-500">{messageForm.message.length}/160 characters</p>
              )}
              <p className="text-xs text-gray-500">
                Variables: <code className="bg-gray-100 px-1">&appointment-date</code>, <code className="bg-gray-100 px-1">&service-name</code>
              </p>
              
              <button
                type="button"
                onClick={handleSendMessage}
                disabled={sendingMessage || !messageForm.message || (messageForm.channel === 'email' && !messageForm.subject)}
                className="w-full px-4 py-2 bg-green-500 hover:bg-green-600 text-white font-semibold rounded-md transition-colors disabled:bg-green-300 flex items-center justify-center"
              >
                {sendingMessage ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <MessageSquare className="w-4 h-4 mr-2" />
                    Send Message
                  </>
                )}
              </button>
            </div>
          )}

          <div className="flex justify-between items-center pt-4 border-t">
            <button
              type="button"
              onClick={() => {
                // Pass appointment data to parent for cancel confirmation
                onClose(); // Close edit modal first
                // Parent will handle showing cancel confirmation
                if (typeof onCancelClick === 'function') {
                  onCancelClick(appointment);
                }
              }}
              disabled={loading}
              className="px-6 py-2 bg-red-500 hover:bg-red-600 text-white font-semibold rounded-md transition-colors disabled:bg-red-300"
            >
              Cancel Appointment
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-6 py-2 bg-yellow-500 hover:bg-yellow-600 text-white font-semibold rounded-md transition-colors disabled:bg-yellow-300"
            >
              {loading ? 'Updating...' : 'Update Appointment'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditAppointmentModal;
